import React from "react";
import { Route, Routes } from "react-router-dom";
import EducationRouteGuard from "../Guards/EducationRouteGuard";
import {
  canAccessEducationAcademicNav,
  canAccessEducationAuditNav,
  canAccessEducationFinanceNav,
  canAccessEducationInventoryNav,
  canAccessEducationProcurementNav,
  canAccessEducationSettingsNav
} from "../Navigation/constants/educationNavRoleAccess";
import EducationHomePage from "./EducationHomePage";
import AcademicSetupPage from "./Academic/AcademicSetupPage";
import ProgramList from "./Academic/Programs/ProgramList";
import ProgramDetail from "./Academic/Programs/ProgramDetail";
import GradeList from "./Academic/Grades/GradeList";
import GradeDetail from "./Academic/Grades/GradeDetail";
import StreamList from "./Academic/Streams/StreamList";
import StreamDetail from "./Academic/Streams/StreamDetail";
import BillingPeriodList from "./Academic/Billing/BillingPeriodList";
import BillingPeriodDetail from "./Academic/Billing/BillingPeriodDetail";
import DepartmentList from "./Academic/Departments/DepartmentList";
import DepartmentDetail from "./Academic/Departments/DepartmentDetail";
import StudentCategoryList from "./Academic/Categories/StudentCategoryList";
import StudentCategoryDetail from "./Academic/Categories/StudentCategoryDetail";
import StudentList from "./Students/StudentList";
import StudentDetail from "./Students/StudentDetail";
import FeeStructureList from "./Fees/FeeStructureList";
import BillingPreviewPage from "./Billing/BillingPreviewPage";
import StockUsagePage from "./StockUsage/StockUsagePage";
import ProcurementPage from "./Procurement/ProcurementPage";
import ExpensePage from "./Expenses/ExpensePage";
import EducationReportsPage from "./Reports/EducationReportsPage";
import EducationSettingsPage from "./Settings/EducationSettingsPage";

const academicGuard = (element: React.ReactNode) => (
  <EducationRouteGuard canAccess={canAccessEducationAcademicNav}>
    {element}
  </EducationRouteGuard>
);

/** Nested routes under `/portal/education/*`. */
const EducationRoutes: React.FC = () => (
  <Routes>
    <Route index element={<EducationHomePage />} />
    <Route path="academic" element={academicGuard(<AcademicSetupPage />)} />
    <Route path="programs" element={academicGuard(<ProgramList />)} />
    <Route
      path="programs/:programId"
      element={academicGuard(<ProgramDetail />)}
    />
    <Route path="grades" element={academicGuard(<GradeList />)} />
    <Route path="grades/:gradeId" element={academicGuard(<GradeDetail />)} />
    <Route path="streams" element={academicGuard(<StreamList />)} />
    <Route path="streams/:streamId" element={academicGuard(<StreamDetail />)} />
    <Route
      path="billing-periods"
      element={academicGuard(<BillingPeriodList />)}
    />
    <Route
      path="billing-periods/:periodId"
      element={academicGuard(<BillingPeriodDetail />)}
    />
    <Route path="departments" element={academicGuard(<DepartmentList />)} />
    <Route
      path="departments/:departmentId"
      element={academicGuard(<DepartmentDetail />)}
    />
    <Route
      path="student-categories"
      element={academicGuard(<StudentCategoryList />)}
    />
    <Route
      path="student-categories/:categoryId"
      element={academicGuard(<StudentCategoryDetail />)}
    />
    <Route path="students" element={academicGuard(<StudentList />)} />
    <Route
      path="students/:studentId"
      element={academicGuard(<StudentDetail />)}
    />
    <Route
      path="fees"
      element={
        <EducationRouteGuard canAccess={canAccessEducationFinanceNav}>
          <FeeStructureList />
        </EducationRouteGuard>
      }
    />
    <Route
      path="billing"
      element={
        <EducationRouteGuard canAccess={canAccessEducationFinanceNav}>
          <BillingPreviewPage />
        </EducationRouteGuard>
      }
    />
    <Route
      path="stock-usage"
      element={
        <EducationRouteGuard canAccess={canAccessEducationInventoryNav}>
          <StockUsagePage />
        </EducationRouteGuard>
      }
    />
    <Route
      path="procurement"
      element={
        <EducationRouteGuard canAccess={canAccessEducationProcurementNav}>
          <ProcurementPage />
        </EducationRouteGuard>
      }
    />
    <Route
      path="expenses"
      element={
        <EducationRouteGuard canAccess={canAccessEducationFinanceNav}>
          <ExpensePage />
        </EducationRouteGuard>
      }
    />
    <Route
      path="reports"
      element={
        <EducationRouteGuard canAccess={canAccessEducationAuditNav}>
          <EducationReportsPage />
        </EducationRouteGuard>
      }
    />
    <Route
      path="settings"
      element={
        <EducationRouteGuard canAccess={canAccessEducationSettingsNav}>
          <EducationSettingsPage />
        </EducationRouteGuard>
      }
    />
  </Routes>
);

export default EducationRoutes;
